import { useState, useEffect, useMemo, useRef } from 'react'
import { useNls } from '../../nls/nlsProvider'
import { useOverviewData } from './useOverviewData'
import { useWorkspace } from '../../context/useWorkspace'
import { useAuth } from '../../context/useAuth'
import { useTimeline } from '../../context/useTimeline'
import { isPersonalWorkspace } from '../../utils/workspaceKind'
import { isModuleEnabled } from '../../utils/workspaceModules'
import {
  canActOn,
  type TeamPersonRow,
  type GoalProgressRow,
  type InsightChecklistItem,
} from '../../utils/overviewInsights'
import { createDefaultOverviewV2 } from '../../services/overviewService'
import type { OverviewDocumentV2, OverviewSectionRef } from '../../types/overview'
import type { OverviewSummaryFeedItem } from '../../services/overviewService'
import {
  loadTeachingOverviewMetrics,
  type TeachingOverviewMetrics,
} from '../../services/eduTimetableService'
import { Avatar } from '../Common/Avatar'
import { Button } from '../Common/Button'
import { IconButton } from '../Common/IconButton'
import { Pressable } from '../Common/Pressable'
import { BottomSheet } from '../Common/BottomSheet'
import { PullToRefresh } from '../Common/PullToRefresh'
import { useFocusTrap } from '../../utils/useFocusTrap'
import { HeaderSection } from './sections/Header'
import { PulseSection } from './sections/Pulse'
import { StackedBar } from './sections/People'
import { AttentionSection } from './sections/Attention'
import { GoalsSection } from './sections/Goals'
import { ActivitySection } from './sections/Activity'
import { ModulesSection } from './sections/Modules'
import { CustomSection } from './sections/CustomSection'
import { MemberProfileModal } from '../Members/MemberProfileModal'
import { useChatEntryForMember } from '../Members/useChatEntryForMember'
import './overviewPage.css'
import './overviewPhone.css'

export interface OverviewPhonePageProps {
  variant: 'member' | 'helper'
  workspaceId?: string
  onBack?: () => void
  onNavigateToAnalysis?: (tab: 'TEAM' | 'WORK') => void
  onOpenModule?: (moduleKey: string) => void
  onOpenInTasks?: () => void
}

export const OverviewPhonePage = ({
  variant,
  workspaceId: propWorkspaceId,
  onBack,
  onNavigateToAnalysis,
  onOpenModule,
  onOpenInTasks,
}: OverviewPhonePageProps) => {
  const { t } = useNls()
  const { workspaces, activeWorkspaceId } = useWorkspace()
  const { currentUser } = useAuth()
  const { checklistItems, isAccessRestricted } = useTimeline()

  const effectiveWorkspaceId = propWorkspaceId || activeWorkspaceId || ''
  const workspace = workspaces.find((w) => w.id === effectiveWorkspaceId) ?? null

  const overviewData = useOverviewData({
    workspaceId: effectiveWorkspaceId,
    workspace,
    uid: currentUser?.uid,
    role: variant === 'helper' ? 'HELPER' : undefined,
  })

  const {
    tier: hookTier,
    health,
    overviewDoc: loadedDoc,
    summary,
    reliabilityLeaderboard,
    reliabilityStats,
    you,
    teamStats,
    people,
    attention,
    goals,
    upcoming,
    activityFeed,
    modules,
    refresh,
  } = overviewData

  const tier = variant === 'helper' ? 'helper' : hookTier

  const [nowMs] = useState(() => Date.now())
  const [selectedPerson, setSelectedPerson] = useState<TeamPersonRow | null>(null)
  const [profileTargetUid, setProfileTargetUid] = useState<string | null>(null)
  const [teachingMetrics, setTeachingMetrics] = useState<TeachingOverviewMetrics | null>(null)
  const sheetContentRef = useRef<HTMLDivElement | null>(null)
  const { openPrivateChat, canOpenPrivate } = useChatEntryForMember()

  useFocusTrap(Boolean(selectedPerson && tier === 'lead'), sheetContentRef)

  const budgetEnabled = isModuleEnabled(workspace, 'budget')
  const stockEnabled = isModuleEnabled(workspace, 'stock')
  const teachingEnabled =
    isModuleEnabled(workspace, 'teaching') || isModuleEnabled(workspace, 'training')
  const workspaceTimezone = workspace?.timezone
  const restrictedUid = isAccessRestricted && currentUser?.uid ? currentUser.uid : undefined

  useEffect(() => {
    if (!teachingEnabled || !effectiveWorkspaceId || tier === 'helper') {
      return undefined
    }
    let active = true
    loadTeachingOverviewMetrics(effectiveWorkspaceId, {
      timezone: workspaceTimezone,
      restrictedUid,
    })
      .then((res) => {
        if (active) setTeachingMetrics(res)
      })
      .catch(() => undefined)
    return () => {
      active = false
    }
  }, [teachingEnabled, effectiveWorkspaceId, workspaceTimezone, restrictedUid, tier])

  const safeAttention = useMemo(() => attention ?? [], [attention])
  const safeUpcoming = useMemo(() => upcoming ?? [], [upcoming])
  const safePeople = useMemo(() => people ?? [], [people])
  const safeTeamStats = useMemo(
    () =>
      teamStats ?? {
        open: 0,
        done7d: 0,
        overdue: 0,
        unassignedOpen: 0,
        activePeopleCount: 0,
        medianOpen: 0,
        overloadedThreshold: 6,
        people: [],
      },
    [teamStats],
  )

  const safeGoalsList: GoalProgressRow[] = useMemo(() => {
    if (Array.isArray(goals)) return goals
    return goals?.goals ?? []
  }, [goals])

  const goalProgressPercent = useMemo(() => {
    if (Array.isArray(goals)) {
      return goals.length > 0
        ? Math.round(goals.reduce((acc, g) => acc + g.progress, 0) / goals.length)
        : 0
    }
    return goals?.averageProgress ?? 0
  }, [goals])

  const safeFeed = useMemo(
    () => (activityFeed as OverviewSummaryFeedItem[] | null) ?? null,
    [activityFeed],
  )

  const activeDoc: OverviewDocumentV2 = useMemo(() => {
    if (loadedDoc && loadedDoc.schemaVersion === 2) {
      return loadedDoc
    }
    return createDefaultOverviewV2(workspace ?? effectiveWorkspaceId, {
      userId: currentUser?.uid ?? '',
      userName: currentUser?.displayName ?? 'Anonymous',
    })
  }, [loadedDoc, workspace, effectiveWorkspaceId, currentUser?.uid, currentUser?.displayName])

  const canActOnCount = useMemo(() => {
    if (tier !== 'lead' || !currentUser?.uid) return 0
    return safeAttention.filter((item) =>
      canActOn(item, {
        uid: currentUser.uid,
        tier,
        isOwner: Boolean(workspace && workspace.ownerId === currentUser.uid),
      }),
    ).length
  }, [safeAttention, currentUser, tier, workspace])

  const helperReliabilityMap = useMemo(() => {
    const map: Record<string, number> = {}
    if (reliabilityLeaderboard) {
      for (const row of reliabilityLeaderboard) {
        if (typeof row.reliabilityScore === 'number') {
          map[row.uid] = row.reliabilityScore
        }
      }
    }
    if (reliabilityStats && typeof reliabilityStats.reliabilityScore === 'number') {
      map[reliabilityStats.uid] = reliabilityStats.reliabilityScore
    }
    return map
  }, [reliabilityLeaderboard, reliabilityStats])

  const formatLastActive = (personUid: string, fallbackMs?: number | null): string => {
    const summaryIso = summary?.people?.[personUid]?.lastActiveAt
    const ms = summaryIso
      ? Date.parse(summaryIso)
      : typeof fallbackMs === 'number'
        ? fallbackMs
        : NaN
    if (!Number.isFinite(ms)) return ''
    const diffMin = Math.max(0, Math.floor((nowMs - ms) / 60000))
    if (diffMin < 1) return t('NLSOverviewPhoneActiveNow')
    if (diffMin < 60) return `${diffMin}m ago`
    const diffH = Math.floor(diffMin / 60)
    if (diffH < 24) return `${diffH}h ago`
    return `${Math.floor(diffH / 24)}d ago`
  }

  const formatShortDue = (due?: string | number | Date | null) => {
    if (!due) return '—'
    const ms =
      typeof due === 'string' || typeof due === 'number'
        ? new Date(due).getTime()
        : due.getTime()
    if (!Number.isFinite(ms)) return '—'
    const d = new Date(ms)
    return `${d.getDate()} ${d.toLocaleDateString(undefined, { month: 'short' })}`
  }

  const isItemOverdue = (due?: string | number | Date | null) => {
    if (!due || nowMs === 0) return false
    const ms =
      typeof due === 'string' || typeof due === 'number'
        ? new Date(due).getTime()
        : due.getTime()
    return Number.isFinite(ms) && ms < nowMs
  }

  const selectedPersonItems: InsightChecklistItem[] = useMemo(() => {
    if (!selectedPerson) return []
    return (checklistItems as InsightChecklistItem[]).filter(
      (item) => item.assigneeId === selectedPerson.uid,
    )
  }, [checklistItems, selectedPerson])

  // Personal Space guard (OV-P12)
  if (workspace && isPersonalWorkspace(workspace)) {
    return (
      <div className="overviewPhone" data-testid="overview-phone-page">
        <div className="overviewEmptyCard" data-testid="overview-personal-space-empty">
          <h3 className="overviewEmptyCard__title">{t('NLSOverviewPersonalSpaceTitle')}</h3>
          <p className="overviewEmptyCard__desc">{t('NLSOverviewPersonalSpaceDesc')}</p>
          {onBack && (
            <Button variant="primary" onClick={onBack}>
              {t('NLSOverviewBackToDashboard')}
            </Button>
          )}
        </div>
      </div>
    )
  }

  const renderMeCard = (title: string, roleLabel: string, reliabilityScore?: number | null) => {
    const open = you?.open ?? 0
    const done = you?.done7d ?? 0
    const overdue = you?.overdue ?? 0
    const lastActive = currentUser?.uid
      ? formatLastActive(currentUser.uid, you?.lastActiveAt)
      : ''
    const displayName = currentUser?.displayName ?? t('NLSOverviewYouFallback')

    return (
      <div className="overviewPhone__card" data-testid="overview-phone-me-card">
        <header className="overviewPhone__cardHeader">
          <h3 className="overviewPhone__cardTitle">{title}</h3>
          {reliabilityScore != null && (
            <span className="overviewPill overviewPill--info">
              {t('NLSOverviewHelperReliabilityPill', { score: reliabilityScore })}
            </span>
          )}
        </header>
        <div className="overviewPhone__meRow">
          <Avatar name={displayName} size="md" />
          <div>
            <strong className="overviewPhone__personName">{displayName}</strong>
            <div className="overviewPhone__personMeta">
              {lastActive ? `${roleLabel} · ${lastActive}` : roleLabel}
            </div>
          </div>
        </div>
        <StackedBar done={done} open={open} overdue={overdue} />
        <div className="overviewPhone__nums3">
          <div className="overviewPhone__nums3Panel">
            <strong className="overviewPhone__nums3Val">{open}</strong>
            <span className="overviewPhone__nums3Label">{t('NLSOverviewLegendOpen')}</span>
          </div>
          <div className="overviewPhone__nums3Panel">
            <strong className="overviewPhone__nums3Val">{done}</strong>
            <span className="overviewPhone__nums3Label">{t('NLSOverviewLegendDone')}</span>
          </div>
          <div className="overviewPhone__nums3Panel">
            <strong
              className={`overviewPhone__nums3Val ${
                overdue > 0 ? 'overviewPhone__nums3Val--danger' : ''
              }`}
            >
              {overdue}
            </strong>
            <span className="overviewPhone__nums3Label">{t('NLSOverviewLegendOverdue')}</span>
          </div>
        </div>
      </div>
    )
  }

  const renderLeadPeopleSection = () => (
    <div className="overviewPhone__card" data-testid="overview-people-section">
      <header className="overviewPhone__cardHeader">
        <h3 className="overviewPhone__cardTitle">
          {t('NLSOverviewPeopleTitle')}
          <span className="overviewPill overviewPill--neutral">{safePeople.length}</span>
        </h3>
        {onNavigateToAnalysis && (
          <Pressable
            type="button"
            className="overviewPhone__link"
            onClick={() => onNavigateToAnalysis('TEAM')}
          >
            {t('NLSOverviewPhoneAnalysisShortLink')}
          </Pressable>
        )}
      </header>
      <div className="overviewPhone__peopleList">
        {safePeople.map((person) => {
          const rel = helperReliabilityMap[person.uid]
          const lastActive = formatLastActive(person.uid, person.lastActiveAt)
          const roleText = person.role ?? t('NLSOverviewRoleMember')
          const flagText =
            person.flag === 'overloaded'
              ? t('NLSOverviewFlagOverloaded')
              : person.flag === 'quiet'
                ? t('NLSOverviewFlagQuiet')
                : null

          return (
            <Pressable
              key={person.uid}
              type="button"
              className="overviewPhone__personRow"
              data-testid={`phone-person-row-${person.uid}`}
              data-person={person.uid}
              onClick={() => setSelectedPerson(person)}
            >
              <Avatar name={person.displayName} size="md" />
              <div className="overviewPhone__personInfo">
                <b className="overviewPhone__personName">{person.displayName}</b>
                <small className="overviewPhone__personMeta">
                  {lastActive ? `${roleText} · ${lastActive}` : roleText}
                </small>
                <StackedBar
                  done={person.done7d}
                  open={person.open}
                  overdue={person.overdue}
                />
              </div>
              <div className="overviewPhone__personRight">
                <div className="overviewPhone__nums">
                  <span className="overviewPhone__numCell">
                    {person.open}
                    <em className="overviewPhone__numLabel">{t('NLSOverviewLegendOpen')}</em>
                  </span>
                  <span className="overviewPhone__numCell">
                    {person.done7d}
                    <em className="overviewPhone__numLabel">
                      {t('NLSOverviewPhoneLegendDoneShort')}
                    </em>
                  </span>
                  <span
                    className={`overviewPhone__numCell ${
                      person.overdue > 0 ? 'overviewPhone__numCell--danger' : ''
                    }`}
                  >
                    {person.overdue}
                    <em className="overviewPhone__numLabel">
                      {t('NLSOverviewPhoneLegendLateShort')}
                    </em>
                  </span>
                </div>
                {(flagText || rel != null) && (
                  <div className="overviewPhone__badges">
                    {flagText && (
                      <span
                        className={`overviewPill overviewPhone__pillCompact ${
                          person.flag === 'overloaded'
                            ? 'overviewPill--bad'
                            : 'overviewPill--warn'
                        }`}
                      >
                        {flagText}
                      </span>
                    )}
                    {rel != null && (
                      <span
                        className="overviewPill overviewPill--info overviewPhone__pillCompact"
                        title={t('NLSOverviewLegendReliability')}
                      >
                        ★ {rel}
                      </span>
                    )}
                  </div>
                )}
              </div>
            </Pressable>
          )
        })}
      </div>
    </div>
  )

  const renderViewerPeopleSection = () => {
    const teammates = safePeople.filter((p) => p.uid !== currentUser?.uid)
    return (
      <div data-testid="overview-people-section" className="flex flex-col gap-3">
        {renderMeCard(t('NLSOverviewViewerYourWeek'), t('NLSOverviewRoleViewer'))}
        <div className="overviewPhone__card">
          <header className="overviewPhone__cardHeader">
            <h3 className="overviewPhone__cardTitle">{t('NLSOverviewViewerTeam')}</h3>
          </header>
          <div className="overviewPhone__teamTotalsRow">
            <div>
              <strong className="overviewPhone__teamTotalVal">{safeTeamStats.open}</strong>
              <div className="overviewPhone__teamTotalLabel">{t('NLSOverviewLegendOpen')}</div>
            </div>
            <div>
              <strong className="overviewPhone__teamTotalVal">{safeTeamStats.done7d}</strong>
              <div className="overviewPhone__teamTotalLabel">{t('NLSOverviewLegendDone')}</div>
            </div>
            <div>
              <strong
                className={`overviewPhone__teamTotalVal ${
                  safeTeamStats.overdue > 0 ? 'overviewPhone__nums3Val--danger' : ''
                }`}
              >
                {safeTeamStats.overdue}
              </strong>
              <div className="overviewPhone__teamTotalLabel">
                {t('NLSOverviewLegendOverdue')}
              </div>
            </div>
          </div>
          {teammates.length > 0 && (
            <div className="overviewPhone__teammateChips">
              {teammates.map((p) => (
                <span key={p.uid} className="overviewPill overviewPill--neutral">
                  <Avatar name={p.displayName} size="xs" />
                  <span>{p.displayName.split(' ')[0]}</span>
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    )
  }

  const renderHelperTeamPulse = () => (
    <div className="overviewPhone__card" data-testid="overview-helper-team-pulse">
      <header className="overviewPhone__cardHeader">
        <h3 className="overviewPhone__cardTitle">{t('NLSOverviewHelperTeamPulse')}</h3>
      </header>
      <div className="overviewPhone__teamTotalsRow">
        <div>
          <strong className="overviewPhone__teamTotalVal">
            {Math.round(goalProgressPercent)}%
          </strong>
          <div className="overviewPhone__teamTotalLabel">
            {t('NLSOverviewGoalProgressRing')}
          </div>
        </div>
        <div>
          <strong className="overviewPhone__teamTotalVal">{safeTeamStats.done7d}</strong>
          <div className="overviewPhone__teamTotalLabel">{t('NLSOverviewPulseDone7d')}</div>
        </div>
      </div>
    </div>
  )

  const renderHelperOverdue = () => {
    const overdueItems = safeAttention.filter((item) => item.kind === 'item_overdue')
    return (
      <div
        className="overviewPhone__card overviewAttn"
        data-testid="overview-helper-overdue-section"
      >
        <header className="overviewPhone__cardHeader">
          <h3 className="overviewPhone__cardTitle">
            {t('NLSOverviewHelperYourOverdue')}
            <span
              className={`overviewPill ${
                overdueItems.length > 0 ? 'overviewPill--bad' : 'overviewPill--neutral'
              }`}
            >
              {overdueItems.length}
            </span>
          </h3>
        </header>
        <div className="overviewAttnList">
          {overdueItems.map((item) => (
            <div key={item.id} className="overviewAttnItem">
              <div className="overviewAttnBar" style={{ background: 'var(--danger)' }} />
              <div className="overviewAttnContent">
                <div className="overviewAttnTitle">{item.title}</div>
                <div className="overviewAttnDetail">{item.detail}</div>
              </div>
            </div>
          ))}
        </div>
        <Button
          variant="primary"
          size="md"
          onClick={() => (onOpenInTasks ? onOpenInTasks() : onBack?.())}
        >
          {t('NLSOverviewHelperOpenInTasks')}
        </Button>
      </div>
    )
  }

  const renderSection = (sectionRef: OverviewSectionRef) => {
    if (sectionRef.hidden) {
      return null
    }

    if (sectionRef.kind === 'CUSTOM') {
      const custom = activeDoc.customSections.find((c) => c.id === sectionRef.id)
      if (!custom) return null
      return (
        <div
          key={`sec-custom-${custom.id}`}
          data-overview-section={`CUSTOM:${custom.id}`}
        >
          <CustomSection
            section={custom}
            tier={tier}
            people={safePeople}
            workspaceId={effectiveWorkspaceId}
            isPhone
          />
        </div>
      )
    }

    // BUILTIN section rendering per tier
    switch (sectionRef.key) {
      case 'HEADER':
        return (
          <div key="sec-header" data-overview-section="BUILTIN:HEADER">
            <HeaderSection
              workspaceName={workspace?.name}
              brief={activeDoc.brief}
              health={health}
              canActOnCount={canActOnCount}
              tier={tier}
              goalProgressPercent={goalProgressPercent}
              isEditing={false}
            />
          </div>
        )

      case 'PULSE':
        if (tier === 'helper') {
          const helperRelScore =
            reliabilityStats?.reliabilityScore ??
            (currentUser?.uid ? helperReliabilityMap[currentUser.uid] : undefined) ??
            null
          return (
            <div
              key="sec-pulse"
              data-overview-section="BUILTIN:PULSE"
              className="flex flex-col gap-3"
            >
              {renderMeCard(
                t('NLSOverviewHelperMyWeek'),
                t('NLSOverviewRoleMember'),
                helperRelScore,
              )}
              {renderHelperTeamPulse()}
            </div>
          )
        }
        return (
          <div key="sec-pulse" data-overview-section="BUILTIN:PULSE">
            <PulseSection
              done7d={safeTeamStats.done7d}
              overdue={safeTeamStats.overdue}
              openCount={safeTeamStats.open}
              activeCount={summary?.activeCount7d ?? safeTeamStats.activePeopleCount}
              totalMembers={safePeople.length || 1}
              budgetPercent={
                modules?.budget
                  ? (((modules.budget as { totalActualSpend?: number }).totalActualSpend ?? 0) /
                      Math.max(
                        1,
                        (modules.budget as { totalBudgetLimit?: number }).totalBudgetLimit ?? 1,
                      )) *
                    100
                  : null
              }
            />
          </div>
        )

      case 'PEOPLE':
        if (tier === 'helper') return null
        return (
          <div key="sec-people" data-overview-section="BUILTIN:PEOPLE">
            {tier === 'lead' ? renderLeadPeopleSection() : renderViewerPeopleSection()}
          </div>
        )

      case 'ATTENTION':
        if (tier === 'helper') {
          return (
            <div key="sec-attention" data-overview-section="BUILTIN:ATTENTION">
              {renderHelperOverdue()}
            </div>
          )
        }
        return (
          <div key="sec-attention" data-overview-section="BUILTIN:ATTENTION">
            <AttentionSection tier={tier} items={safeAttention} />
          </div>
        )

      case 'GOALS':
        if (tier === 'helper') return null
        return (
          <div key="sec-goals" data-overview-section="BUILTIN:GOALS">
            <GoalsSection
              goals={safeGoalsList}
              upcoming={safeUpcoming}
              onNavigateToAnalysis={onNavigateToAnalysis}
            />
          </div>
        )

      case 'ACTIVITY':
        if (tier === 'helper') return null
        return (
          <div key="sec-activity" data-overview-section="BUILTIN:ACTIVITY">
            <ActivitySection tier={tier} feed={safeFeed} />
          </div>
        )

      case 'MODULES': {
        if (tier === 'helper') return null
        const modulesActive = budgetEnabled || stockEnabled || teachingEnabled
        if (!modulesActive) return null
        return (
          <div key="sec-modules" data-overview-section="BUILTIN:MODULES">
            <ModulesSection
              stockEnabled={stockEnabled}
              stockItems={modules?.stock as unknown as []}
              budgetEnabled={budgetEnabled}
              budgetSummary={modules?.budget as unknown as null}
              teachingEnabled={teachingEnabled}
              teachingMetrics={teachingMetrics}
              onOpenModule={onOpenModule}
            />
          </div>
        )
      }

      default:
        return null
    }
  }

  return (
    <PullToRefresh onRefresh={refresh}>
      <div className="overviewPhone" data-testid="overview-phone-page">
        {activeDoc.sections.map(renderSection)}

        {tier === 'lead' && (
          <div className="overviewPhone__hint" data-testid="overview-phone-customize-hint">
            <i className="overviewPhone__hintIcon" aria-hidden="true">
              ✎
            </i>
            <span>{t('NLSOverviewPhoneCustomizeWebHint')}</span>
          </div>
        )}

        {/* Person Bottom Sheet (lead only) */}
        <BottomSheet
          isOpen={Boolean(selectedPerson && tier === 'lead')}
          onClose={() => setSelectedPerson(null)}
          maxHeightSvh={78}
          className="overviewPersonBottomSheet"
        >
          {selectedPerson && (
            <div ref={sheetContentRef} data-testid="phone-person-bottom-sheet">
              <div className="overviewPhoneSheet__header">
                <Avatar name={selectedPerson.displayName} size="md" />
                <div>
                  <h3 className="overviewPhoneSheet__title">{selectedPerson.displayName}</h3>
                  <span className="overviewPhoneSheet__meta">
                    {(() => {
                      const roleStr = selectedPerson.role ?? t('NLSOverviewRoleMember')
                      const lastStr = formatLastActive(
                        selectedPerson.uid,
                        selectedPerson.lastActiveAt,
                      )
                      return lastStr ? `${roleStr} · ${lastStr}` : roleStr
                    })()}
                  </span>
                </div>
                <div style={{ marginLeft: 'auto' }}>
                  <IconButton
                    data-autofocus
                    icon="✕"
                    label={t('NLSOverviewPersonSheetClose')}
                    variant="tertiary"
                    size="md"
                    onClick={() => setSelectedPerson(null)}
                  />
                </div>
              </div>

              <div className="overviewPhone__nums3">
                <div className="overviewPhone__nums3Panel">
                  <strong className="overviewPhone__nums3Val">{selectedPerson.open}</strong>
                  <span className="overviewPhone__nums3Label">
                    {t('NLSOverviewLegendOpen')}
                  </span>
                </div>
                <div className="overviewPhone__nums3Panel">
                  <strong className="overviewPhone__nums3Val">{selectedPerson.done7d}</strong>
                  <span className="overviewPhone__nums3Label">
                    {t('NLSOverviewLegendDone')}
                  </span>
                </div>
                <div className="overviewPhone__nums3Panel">
                  <strong
                    className={`overviewPhone__nums3Val ${
                      selectedPerson.overdue > 0 ? 'overviewPhone__nums3Val--danger' : ''
                    }`}
                  >
                    {selectedPerson.overdue}
                  </strong>
                  <span className="overviewPhone__nums3Label">
                    {t('NLSOverviewLegendOverdue')}
                  </span>
                </div>
              </div>

              <table className="overviewPhoneSheet__table">
                <thead>
                  <tr>
                    <th>{t('NLSOverviewPersonSheetAssignedItemHeader')}</th>
                    <th>{t('NLSOverviewPersonSheetDueHeader')}</th>
                    <th aria-label={t('NLSOverviewPersonSheetStatusHeader')} />
                  </tr>
                </thead>
                <tbody>
                  {selectedPersonItems.length === 0 ? (
                    <tr>
                      <td colSpan={3} style={{ color: 'var(--muted)', textAlign: 'center' }}>
                        {t('NLSOverviewPersonSheetEmptyItems')}
                      </td>
                    </tr>
                  ) : (
                    selectedPersonItems.slice(0, 10).map((item) => {
                      const itemDue = item.dueAt ?? item.endDate ?? item.scheduleEndDate
                      const overdue = isItemOverdue(itemDue)
                      const completed = item.isCompleted || item.workflowStatus === 'COMPLETE'
                      const blocked =
                        item.boardStatus === 'BLOCKED' || item.workflowStatus === 'BLOCKED'

                      const statusClass = completed
                        ? 'overviewPill--good'
                        : overdue
                          ? 'overviewPill--bad'
                          : blocked
                            ? 'overviewPill--warn'
                            : 'overviewPill--info'

                      const statusLabel = completed
                        ? t('NLSOverviewLegendDone')
                        : overdue
                          ? t('NLSOverviewPillLate')
                          : blocked
                            ? t('NLSOverviewPillBlocked')
                            : t('NLSOverviewPillOpen')

                      return (
                        <tr key={item.id}>
                          <td style={{ color: 'var(--text-strong)', fontWeight: 600 }}>
                            {item.title ?? item.text ?? t('NLSOverviewUntitledItem')}
                          </td>
                          <td style={{ fontFamily: 'var(--font-figures)' }}>
                            {formatShortDue(itemDue)}
                          </td>
                          <td>
                            <span className={`overviewPill ${statusClass}`}>
                              {statusLabel}
                            </span>
                          </td>
                        </tr>
                      )
                    })
                  )}
                </tbody>
              </table>

              <div className="overviewPhoneSheet__actions">
                {canOpenPrivate && (
                  <Button
                    variant="primary"
                    size="md"
                    onClick={() =>
                      void openPrivateChat(selectedPerson.uid, selectedPerson.displayName)
                    }
                  >
                    {t('NLSOverviewPersonSheetMessage')}
                  </Button>
                )}
                <Button
                  variant="secondary"
                  size="md"
                  onClick={() => setProfileTargetUid(selectedPerson.uid)}
                >
                  {t('NLSOverviewPersonSheetProfileShort')}
                </Button>
              </div>
            </div>
          )}
        </BottomSheet>

        {/* Member Profile Modal */}
        {profileTargetUid && (
          <MemberProfileModal
            userId={profileTargetUid}
            onClose={() => setProfileTargetUid(null)}
          />
        )}
      </div>
    </PullToRefresh>
  )
}
export default OverviewPhonePage
